import { useEffect } from 'react';
import { css } from '@emotion/react';

import { makeStyles } from '@toolbox/style';
import { BookedSlot } from '@widget/presets/ThankYouStep/BookedSlot';
import { Button, Container, Heading, LinkButton, Text, Box } from '@toolbox/components';
import {
  Step,
  useCloseWidget,
  useLeadData,
  useWidgetContext,
  useZipCodeLocation,
} from '@widget/core';
import { ActionButton, QuestionHeader } from '../../components';
import { defaultValues } from './utils';
import { ValuationHeaderV3 } from './ValuationHeaderV3';
import { checkIfObjectContainsAtLeastOneValue } from '../../utils/objectUtils';
import { formatMcSearchLink } from './utils/formatMcSearchLink';
import { PhoneLinkMUIWrapper } from '@toolbox/components/PhoneLink';

import type { ReactNode } from 'react';
import type { LeadData, StepProps } from '@widget/core';
import type { LinkType } from '@widget/types/LinkType';
import type { BookedSlotType } from '@widget/api/types';
import type { KeyValueType } from '@widget/types/KeyValueType';
import type { PhoneCta } from '@widget/types/PhoneCtaType';

export type ThankYouStepProps = StepProps & {
  heading?: string;
  title?: string;
  description?: string;
  buttonText?: string;
  closeButtonText?: string;
  bookCallButtonText?: string;
  resetButtonText?: string;
  closeWidget?: boolean;
  bookCall?: boolean;
  icon?: ReactNode;
  dataAcquiredText?: string;
  loadingText?: string;
  redirectionAlertText?: string;
  propertySearchLink?: string | null;
  redirectCustomLink?: LinkType;
  enableAutomaticRedirectionToPropertySearch?: KeyValueType[];
  alReadyBookedSlot?: (BookedSlotType & { phone: string; address: string }) | null;
  userAlreadyHasAppointmentErrorText?: string;
  phoneLink?: {
    phoneCtaTarget?: string | null;
    phoneCtaTel?: string | null;
    phoneCtaText?: string | null;
  };
  children: ReactNode;
};

export type ThankYouStepVProps = ThankYouStepProps & {
  phoneCta?: PhoneCta | null;
  showPhoneLink?: boolean;
};

const useStyles = makeStyles(({ theme }) => ({
  root: css`
    display: flex;
    flex-direction: column;
    ${theme.media.up('sm')} {
      padding: 0;
    }
  `,
  success: css`
    background: rgba(255, 255, 255, 0.2);
    color: ${theme.palette.colors.white};
    padding: ${theme.spacing(3, '0')};
    display: flex;
    justify-content: center;
    align-items: center;
  `,
  successText: css`
    margin-left: ${theme.spacing(1)};
    color: ${theme.palette.colors.white};
  `,
  animation: css`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 180px;
    height: 180px;
    margin: ${theme.spacing(10)} auto;
  `,
  body: css`
    text-align: left;
    ${theme.media.up('sm')} {
      margin: auto;
      text-align: center;
    }
  `,
  bodyText: css`
    font-size: 18px;
    font-weight: ${theme.fontWeights.medium};
    padding-top: ${theme.spacing(12)};
  `,
  wrapper: css`
    margin: 24px 0 0;
  `,
  footer: css`
    padding: ${theme.spacing(4)} 0;

    ${theme.media.up('sm')} {
      padding: ${theme.spacing(6, 0, 12)};
      margin: 0 auto;
      max-width: 300px;
      width: 100%;
    }
  `,
  bookCallButton: css`
    padding: ${theme.spacing(4)};

    ${theme.media.up('sm')} {
      padding: ${theme.spacing(0, 0, 12)};
      margin: 0 auto;
      max-width: 440px;
      width: 100%;
    }
  `,
  spacer: css`
    height: ${theme.spacing(12)};
  `,
}));

export const ThankYouStepComponent = ({
  heading = defaultValues.heading,
  title = defaultValues.title,
  description = defaultValues.description,
  buttonText = defaultValues.buttonText,
  bookCallButtonText = defaultValues.bookCallButtonText,
  resetButtonText = defaultValues.resetButtonText,
  closeButtonText,
  closeWidget,
  bookCall,
  enableAutomaticRedirectionToPropertySearch,
  propertySearchLink,
  redirectCustomLink,
  alReadyBookedSlot,
  userAlreadyHasAppointmentErrorText,
  phoneCta,
  showPhoneLink,
  children,
  ...stepProps
}: ThankYouStepVProps): JSX.Element => {
  const classes = useStyles();
  const { activeId, getStepProps, close, enableReset, reset, getFormData } = useWidgetContext();
  const showValuation = !getStepProps(activeId)?.hideLiveValuation;
  const { setLeadData } = useLeadData();
  const formData = getFormData();
  const { setIsThankYouPageShown } = useCloseWidget();
  const { zipCodeLocationData } = useZipCodeLocation();

  const redirectToMcSearch = checkIfObjectContainsAtLeastOneValue(
    formData,
    enableAutomaticRedirectionToPropertySearch,
  );

  if (redirectToMcSearch) {
    setTimeout(() => {
      //added to prevent leave / stay on page popup in firefox
      window.onbeforeunload = null;
      const link = formatMcSearchLink(propertySearchLink, zipCodeLocationData);
      link && window.location.replace(link);
    }, 1500);
  }

  const resetWidget = (): void => {
    setLeadData({} as LeadData);
    reset();
  };

  useEffect(() => {
    setIsThankYouPageShown(true);
  }, [setIsThankYouPageShown]);

  const closeWidgetHandler = (): void => {
    setLeadData({} as LeadData);
    close();
  };

  return (
    <Step {...stepProps}>
      <Container css={classes.root as any}>
        <div>
          <div>
            <QuestionHeader closeButtonText={closeButtonText} title={heading} />
            <div css={classes.spacer as any} />
            <div>
              <Box css={classes.body as any}>
                {showValuation && <ValuationHeaderV3 />}
                <Heading variant="h3" as="p" fw="bold">
                  {title}
                </Heading>
                {alReadyBookedSlot ? (
                  <BookedSlot
                    bookedSlot={alReadyBookedSlot}
                    phone={alReadyBookedSlot.phone}
                    address={alReadyBookedSlot.address}
                    bookedSlotText={userAlreadyHasAppointmentErrorText}
                    appointmentType={alReadyBookedSlot.type}
                  />
                ) : (
                  <Text css={classes.bodyText as any}>{description}</Text>
                )}
              </Box>
              <Box css={classes.wrapper as any}>{children}</Box>
              {closeWidget && (
                <Box css={classes.footer as any}>
                  <Button
                    fullWidth
                    onClick={() => closeWidgetHandler()}
                    variant="secondary"
                    size={'medium'}
                  >
                    {buttonText}
                  </Button>
                </Box>
              )}
              {redirectCustomLink && (
                <Box css={classes.footer as any}>
                  <LinkButton
                    fullWidth
                    size="large"
                    variant="secondary"
                    href={redirectCustomLink.url || ''}
                    target={redirectCustomLink.openInNewWindow ? '_blank' : ''}
                  >
                    {redirectCustomLink.text}
                  </LinkButton>
                </Box>
              )}

              {enableReset && (
                <Box css={classes.footer as any}>
                  <Button fullWidth onClick={resetWidget} variant="secondary" size="medium">
                    {resetButtonText}
                  </Button>
                </Box>
              )}

              {bookCall && (
                <Box css={closeWidget ? (classes.bookCallButton as any) : (classes.footer as any)}>
                  <ActionButton fullWidth skip variant="primary" size="large">
                    {bookCallButtonText}
                  </ActionButton>
                </Box>
              )}

              {showPhoneLink && (
                <Box
                  style={{ paddingTop: '42px', display: 'flex', justifyContent: 'space-around' }}
                >
                  <PhoneLinkMUIWrapper phoneCTA={phoneCta} />
                </Box>
              )}
            </div>
          </div>
        </div>
      </Container>
    </Step>
  );
};
